import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChatCircleText, Plus, UsersThree } from "@phosphor-icons/react";

const CATEGORIES = ["Geral", "Recomendações", "Reviews", "Notícias", "Marketplace", "Off-Topic"];

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "Geral", content: "" });

  async function load() {
    const params = {};
    if (category) params.category = category;
    const { data } = await api.get("/community/topics", { params });
    setTopics(data);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  async function createTopic(e) {
    e.preventDefault();
    if (!user) { toast.error("Entra para criar tópicos"); navigate("/login"); return; }
    try {
      await api.post("/community/topics", form);
      toast.success("Tópico criado");
      setOpen(false);
      setForm({ title: "", category: "Geral", content: "" });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }

  return (
    <div className="pt-20 max-w-5xl mx-auto px-5 lg:px-8 pb-24" data-testid="community-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ FÓRUM</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Comunidade</h1>
          <p className="text-zinc-400 mt-2">Conversa com outros leitores de mangá em Portugal.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-crimson" data-testid="new-topic-btn">
              <Plus size={18} weight="bold" className="mr-2" /> Novo Tópico
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121217] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Criar Tópico</DialogTitle>
            </DialogHeader>
            <form onSubmit={createTopic} className="space-y-4 mt-2">
              <div>
                <Label>Título</Label>
                <Input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1 bg-[#1C1C22] border-white/10" data-testid="topic-title-input" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1 bg-[#1C1C22] border-white/10" data-testid="topic-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121217] border-white/10 text-white">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Conteúdo</Label>
                <Textarea required rows={6} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} className="mt-1 bg-[#1C1C22] border-white/10" data-testid="topic-content-input" />
              </div>
              <Button type="submit" className="btn-crimson w-full" data-testid="topic-submit-btn">Publicar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        <button onClick={() => setCategory("")} className={`px-4 py-2 rounded-lg text-xs font-mono-accent border whitespace-nowrap ${!category ? "bg-[#E63946] border-[#E63946] text-white" : "bg-[#121217] border-white/10 text-zinc-400"}`}>TODOS</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`} className={`px-4 py-2 rounded-lg text-xs font-mono-accent uppercase whitespace-nowrap border ${category === c ? "bg-[#E63946] border-[#E63946] text-white" : "bg-[#121217] border-white/10 text-zinc-400 hover:text-white"}`}>{c}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center font-mono-accent text-xs text-zinc-500 py-10">A CARREGAR...</div>
      ) : topics.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <UsersThree size={42} weight="duotone" className="text-[#7209B7] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem tópicos por aqui</div>
          <div className="text-sm text-zinc-500 mt-1">Sê o primeiro a abrir um tópico.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {topics.map(t => (
            <Link key={t.topic_id} to={`/comunidade/${t.topic_id}`} className="card-hover block bg-[#121217] border border-white/5 rounded-xl p-5" data-testid={`topic-${t.topic_id}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="tag">{t.category}</span>
                    <span className="font-mono-accent text-[10px] text-zinc-500">{new Date(t.created_at).toLocaleDateString("pt-PT")}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight truncate">{t.title}</h3>
                  <div className="text-xs text-zinc-500 mt-1">por <span className="text-zinc-300">{t.author_name}</span></div>
                </div>
                <div className="flex items-center gap-1 text-zinc-400">
                  <ChatCircleText size={18} weight="bold" />
                  <span className="font-mono-accent text-sm">{t.comment_count || 0}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
